/**
 * The parts of the maturity chart that are neither React nor network: building
 * its query string, assigning a colour per crop type, and turning dates into
 * positions on a calendar axis.
 *
 * It is separate from the components because the same chart runs on the public
 * website (`/api/public/maturity-chart`), and the half worth sharing verbatim is
 * this one — a colour scheme that differs between the two would make the public
 * picking calendar and the internal one look like different data.
 */

import type {
  MaturityChartCropTypeFacet,
  MaturityChartEntry,
  MaturityChartFilters,
} from '@/types/cultivar';

// ---------------------------------------------------------------------------
// Filter state
// ---------------------------------------------------------------------------

/** How the timeline stacks its rows. */
export type MaturityGroupBy = 'crop_type' | 'region' | 'none';

/**
 * Everything the chart page lets you change.
 *
 * It lives here beside the query builder rather than in the filter component so
 * that the page, the URL sync and the component all agree on one shape, and so
 * a React file is not the home of a plain constant.
 */
export interface MaturityFilterState {
  seasonYears: number[];
  /** Every season with data, folded onto one season's calendar. Overrides seasonYears. */
  allSeasons: boolean;
  regionIds: number[];
  cropTypeIds: number[];
  fromDate: string;
  toDate: string;
  includeEstimates: boolean;
  groupBy: MaturityGroupBy;
}

export const EMPTY_MATURITY_FILTERS: MaturityFilterState = {
  seasonYears: [],
  allSeasons: false,
  regionIds: [],
  cropTypeIds: [],
  fromDate: '',
  toDate: '',
  includeEstimates: true,
  groupBy: 'crop_type',
};

// ---------------------------------------------------------------------------
// Query string
// ---------------------------------------------------------------------------

/** Lists go over as `region_ids=3,7` so a filtered view is a URL somebody can paste. */
export function maturityChartQuery(filters: MaturityChartFilters): string {
  const params = new URLSearchParams();
  if (filters.allSeasons) params.set('all_seasons', 'true');
  else if (filters.seasonYears?.length) params.set('season_years', filters.seasonYears.join(','));
  if (filters.regionIds?.length) params.set('region_ids', filters.regionIds.join(','));
  if (filters.cropTypeIds?.length) params.set('crop_type_ids', filters.cropTypeIds.join(','));
  if (filters.fromDate) params.set('from_date', filters.fromDate);
  if (filters.toDate) params.set('to_date', filters.toDate);
  // Sent explicitly whenever it is false; the server defaults it to true.
  if (filters.includeEstimates === false) params.set('include_estimates', 'false');
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

// ---------------------------------------------------------------------------
// Colour
// ---------------------------------------------------------------------------

/**
 * Eight categorical hues, in this order.
 *
 * Validated against a white card surface: every slot inside the lightness band
 * and above the chroma floor, worst adjacent colour-vision-deficiency ΔE 9.1
 * (target ≥ 8) and worst normal-vision ΔE 19.6 (floor ≥ 15). Three of them sit
 * under 3:1 contrast on white, which is permitted only because every bar is
 * directly labelled with its cultivar name and date — identity never rests on
 * the colour alone. Do not reorder: the ordering is what clears the CVD gate,
 * not decoration.
 */
export const CROP_TYPE_COLOURS = [
  '#2a78d6', // blue
  '#eb6834', // orange
  '#1baf7a', // aqua
  '#eda100', // yellow
  '#e87ba4', // magenta
  '#008300', // green
  '#4a3aa7', // violet
  '#e34948', // red
] as const;

/** Everything past the eighth crop type. Neutral on purpose — it is not a ninth hue. */
export const OTHER_CROP_TYPE_COLOUR = '#8a8a8a';

export interface CropTypeColourKey {
  /** The id colour is keyed on: the top-level crop type. */
  id: number;
  name: string;
  colour: string;
  /** True once the palette is exhausted and this key shares the neutral grey. */
  isOther: boolean;
}

/**
 * The top-level crop type an entry belongs to.
 *
 * Colour follows the root, so "Apple" and "Apple → Gala group" are one colour
 * and one legend row. Colouring the leaf instead fragments the legend into
 * every sub-type anyone has ever created, which is the opposite of what a
 * colour-coded chart is for. `crop_types` is a two-level hierarchy by design,
 * so the parent is always the root.
 */
export function rootCropType(
  entry: Pick<MaturityChartEntry, 'crop_type_id' | 'crop_type_name' | 'crop_type_parent_id' | 'crop_type_parent_name'>
): { id: number; name: string } | null {
  if (entry.crop_type_parent_id != null) {
    return { id: entry.crop_type_parent_id, name: entry.crop_type_parent_name ?? 'Unnamed' };
  }
  if (entry.crop_type_id != null) {
    return { id: entry.crop_type_id, name: entry.crop_type_name ?? 'Unnamed' };
  }
  return null;
}

/** The bucket entries with no crop type fall into. Negative so it can never collide with a real id. */
export const UNCLASSIFIED_CROP_TYPE_ID = -1;

/**
 * Assign a colour to every top-level crop type in the dataset.
 *
 * Built from the facets, which describe what exists rather than what is
 * currently shown, and ordered alphabetically. Both of those are load-bearing:
 * a colour derived from position in the *filtered* result would repaint every
 * surviving crop type each time a filter changed, so the chart you compared
 * against a minute ago would be a different chart in the same colours.
 *
 * Past eight roots the rest share one neutral grey rather than getting a
 * generated ninth hue, which would land inside the separation the eight were
 * chosen to keep.
 */
export function buildCropTypeColours(
  facets: MaturityChartCropTypeFacet[]
): Map<number, CropTypeColourKey> {
  const roots = new Map<number, string>();
  for (const ct of facets) {
    if (ct.parent_id != null) roots.set(ct.parent_id, ct.parent_name ?? 'Unnamed');
    else roots.set(ct.id, ct.name);
  }

  const ordered = [...roots.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  const out = new Map<number, CropTypeColourKey>();
  ordered.forEach(([id, name], i) => {
    const isOther = i >= CROP_TYPE_COLOURS.length;
    out.set(id, {
      id,
      name,
      colour: isOther ? OTHER_CROP_TYPE_COLOUR : CROP_TYPE_COLOURS[i],
      isOther,
    });
  });

  out.set(UNCLASSIFIED_CROP_TYPE_ID, {
    id: UNCLASSIFIED_CROP_TYPE_ID,
    name: 'No crop type',
    colour: OTHER_CROP_TYPE_COLOUR,
    isOther: true,
  });
  return out;
}

/** The colour key an entry renders in. Never null — an unclassified cultivar still gets a bar. */
export function colourKeyFor(
  entry: MaturityChartEntry,
  colours: Map<number, CropTypeColourKey>
): CropTypeColourKey {
  const root = rootCropType(entry);
  const key = root ? colours.get(root.id) : undefined;
  return key ?? colours.get(UNCLASSIFIED_CROP_TYPE_ID)!;
}

// ---------------------------------------------------------------------------
// Calendar axis
// ---------------------------------------------------------------------------

export const MS_PER_DAY = 86400000;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * A `YYYY-MM-DD` date as a UTC timestamp.
 *
 * Parsed by hand rather than through `new Date(str)`, which reads a bare date
 * as UTC midnight and then renders it in the browser's zone — one timezone west
 * and every pick date shows a day early.
 */
export function toUtc(dateStr: string): number {
  const [y, m, d] = dateStr.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
}

/** "16 Mar". Deliberately not `toLocaleDateString`, which would use the browser's zone. */
export function shortDate(dateStr: string): string {
  const [, m, d] = dateStr.split('-').map(Number);
  return `${d} ${MONTHS[m - 1]}`;
}

/** "16 Mar 2026". */
export function shortDateWithYear(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  return `${d} ${MONTHS[m - 1]} ${y}`;
}

export interface Axis {
  min: number;
  max: number;
  span: number;
  /**
   * Set when more than one season is in view: every date is moved onto this
   * season's calendar before it is placed. Without it three seasons draw as a
   * timeline three years wide with each picking run a hairline; folded, the
   * same cultivar's years sit under each other and can be compared.
   */
  foldToSeason: number | null;
}

/**
 * Where an entry's bar ends.
 *
 * The server sends `window_end_date` on every entry — a recorded last pick, or
 * the first pick plus a harvest window when only a start is known. The
 * fallbacks cover a response from a server that predates it.
 */
export function windowEnd(e: MaturityChartEntry): string {
  return e.window_end_date || e.last_pick_date || e.first_pick_date;
}

/** True when the bar's end was measured rather than assumed from a window length. */
export function hasRecordedEnd(e: MaturityChartEntry): boolean {
  return e.window_source ? e.window_source === 'recorded' : !!e.last_pick_date;
}

/**
 * A date moved by whole seasons onto `toSeason`'s calendar.
 *
 * Shifted by the difference in season years rather than stamped with one, so a
 * December start and a January end stay in order after the move.
 */
function foldedUtc(dateStr: string, entrySeason: number, toSeason: number | null): number {
  if (toSeason == null || toSeason === entrySeason) return toUtc(dateStr);
  const [y, m, d] = dateStr.split('-').map(Number);
  return Date.UTC(y + (toSeason - entrySeason), m - 1, d);
}

/**
 * The axis spans the data plus a few days, so an end bar is not flush to the edge.
 * Seasons fold onto the most recent one whenever more than one is present.
 */
export function buildAxis(entries: MaturityChartEntry[]): Axis | null {
  if (entries.length === 0) return null;
  const seasons = new Set(entries.map((e) => e.season_year));
  const foldToSeason = seasons.size > 1 ? Math.max(...seasons) : null;

  let min = Infinity;
  let max = -Infinity;
  for (const e of entries) {
    const start = foldedUtc(e.first_pick_date, e.season_year, foldToSeason);
    const end = foldedUtc(windowEnd(e), e.season_year, foldToSeason);
    if (start < min) min = start;
    if (end > max) max = end;
  }
  min -= 3 * MS_PER_DAY;
  max += 3 * MS_PER_DAY;
  return { min, max, span: Math.max(max - min, MS_PER_DAY), foldToSeason };
}

/** Where a date sits on the axis, 0–100. Pass the entry's season so a folded axis can move it. */
export function axisPercent(axis: Axis, dateStr: string, seasonYear?: number): number {
  const t = seasonYear == null ? toUtc(dateStr) : foldedUtc(dateStr, seasonYear, axis.foldToSeason);
  return ((t - axis.min) / axis.span) * 100;
}

/** A span of N days as a percentage of the axis — for uncertainty bands. */
export function daysPercent(axis: Axis, days: number): number {
  return (days * MS_PER_DAY / axis.span) * 100;
}

export interface Gridline {
  key: string;
  label: string;
  left: number;
}

/**
 * Month gridlines, so the eye has something to measure against.
 *
 * The label carries the year whenever the axis crosses one. Southern-hemisphere
 * harvests run through New Year, so a bare "Jan" on an axis that starts in
 * December is genuinely ambiguous about which season it belongs to.
 */
export function buildGridlines(axis: Axis | null): Gridline[] {
  if (!axis) return [];
  const lines: Gridline[] = [];
  const start = new Date(axis.min);
  // A folded axis is a season's calendar, not a particular year's, so a year
  // on its labels would name only the season everything was moved onto.
  const spansYears = axis.foldToSeason == null
    && new Date(axis.min).getUTCFullYear() !== new Date(axis.max).getUTCFullYear();
  let y = start.getUTCFullYear();
  let m = start.getUTCMonth();

  // Enough iterations for the widest axis a season filter can produce.
  for (let i = 0; i < 160; i++) {
    const t = Date.UTC(y, m, 1);
    if (t > axis.max) break;
    if (t >= axis.min) {
      lines.push({
        key: `${y}-${m}`,
        label: spansYears && m === 0 ? `Jan ${String(y).slice(2)}` : MONTHS[m],
        left: ((t - axis.min) / axis.span) * 100,
      });
    }
    m += 1;
    if (m > 11) { m = 0; y += 1; }
  }

  // A dense axis would print a label per month on top of itself at phone width.
  // Thinning keeps the ticks readable rather than dropping the axis entirely.
  const step = lines.length > 18 ? Math.ceil(lines.length / 12) : 1;
  return step === 1 ? lines : lines.filter((_, i) => i % step === 0);
}
